import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { never } from "client_legacy/ndaw/errors";
import { HubItemCategoryModel } from "client/Api/Hub/hub";
import { Details } from "./Details/Details";
import { Overview, sortOptions } from "./Overview/Overview";
import { AnnouncementPreviewItemModel } from "client/Api/Hub/Announcements/announcementViewer";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { ActiveFilter, HubItemIsNewFilter } from "../common";
import { newGuid } from "client_legacy/ndaw/guid";
import { ensureNotNil, isNil } from "client/Misc/utils";
import {
	DataSourceSortDirection,
	DataSourceSortableState
} from "client/Data/sorting";
import { HubItemViewLocation } from "client/Api/Hub/common.details";
import { fromArray } from "client_legacy/ndaw/mapHelpers";

export type AnnouncementsView = {
	kind: "overview";
} | {
	kind: "details";
	id: number;
	commentId?: number;
};

export type AnnouncementsPageContentProps = {
	view: AnnouncementsView;
	onViewChange: (view: AnnouncementsView) => void;
	selectedCategoryId: number | null;
	onSelectCategory: (categoryId: number | null) => void;
	categories: readonly HubItemCategoryModel[];
	onCategoryCreated: () => void;
	hubItemKindsCanCreateNewCategoriesFor: ReadonlyArray<HubItemKind>;
	onActiveFilterChange: (activeFilter: ActiveFilter | null) => void;
	activeFilter: ActiveFilter | null;
	canOpenHubManagement: boolean;
};

export const AnnouncementsPageContent = (props: AnnouncementsPageContentProps): ReactElement => {
	const {
		view,
		onViewChange,
		selectedCategoryId,
		onSelectCategory,
		categories,
		onCategoryCreated,
		hubItemKindsCanCreateNewCategoriesFor,
		activeFilter,
		onActiveFilterChange,
		canOpenHubManagement
	} = props;

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid()
	});

	const [sorting, setSorting] = useState<DataSourceSortableState<AnnouncementPreviewItemModel>>(
		ensureNotNil(
			sortOptions.find(x =>
				x.value.direction === DataSourceSortDirection.Descending
				&& x.value.propertyName === "publicationStart"
			)
		)
			?.value
	);

	const [isNewFilter, setIsNewFilter] = useState<HubItemIsNewFilter>("all");

	const navigateToItemHandler = useCallback(
		(id: number): void => {
			onViewChange({
				kind: "details",
				id
			});
		},
		[onViewChange]
	);

	const navigateToOverviewHandler = useCallback(
		(categoryId?: number | null): void => {
			onViewChange({ kind: "overview" });

			if (categoryId !== undefined) {
				onSelectCategory(categoryId);
			}
		},
		[onSelectCategory, onViewChange]
	);

	const activeFilterChangedHandler = useCallback(
		(filter: ActiveFilter | null): void => {
			onActiveFilterChange(filter);

			if (filter !== null) {
				navigateToOverviewHandler(null);
			}
		},
		[navigateToOverviewHandler, onActiveFilterChange]
	);

	const categoryMap = useMemo(
		(): Map<number, HubItemCategoryModel> => {
			return fromArray(
				categories,
				(x: HubItemCategoryModel): number => x.id
			);
		},
		[categories]
	);

	const getCategory = useCallback(
		(categoryId: number | nil): HubItemCategoryModel | undefined => {
			if (isNil(categoryId)) {
				return undefined;
			}

			return categoryMap.get(categoryId);
		},
		[categoryMap]
	);

	switch (view.kind) {
		case "overview":
			return (
				<Overview
					onNavigateToItem={navigateToItemHandler}
					categories={categories}
					getCategory={getCategory}
					selectedCategoryId={selectedCategoryId}
					activeFilter={activeFilter}
					onActiveFilterChange={activeFilterChangedHandler}
					onCategoryCreated={onCategoryCreated}
					hubItemKindsCanCreateNewCategoriesFor={hubItemKindsCanCreateNewCategoriesFor}
					searchTerm={searchTerm}
					onSearchTermChange={setSearchTerm}
					sorting={sorting}
					onSortingChange={setSorting}
					canOpenHubManagement={canOpenHubManagement}
					isNewFilter={isNewFilter}
					onIsNewFilterChange={setIsNewFilter}
				/>
			);
		case "details":
			return (
				<Details
					mode="full"
					announcementId={view.id}
					getCategory={getCategory}
					deepLink={view.commentId
						? {
							kind: "comment",
							commentId: view.commentId
						}
						: undefined
					}
					onNavigateToItem={navigateToItemHandler}
					selectedCategoryId={selectedCategoryId}
					activeFilter={activeFilter}
					onActiveFilterChange={activeFilterChangedHandler}
					onSelectCategory={navigateToOverviewHandler}
					onBack={navigateToOverviewHandler}
					onCategoryCreated={onCategoryCreated}
					searchTerm={searchTerm}
					sorting={sorting}
					viewLocation={HubItemViewLocation.Hub}
					isNewFilter={isNewFilter}
				/>
			);
		default:
			never(view);
	}
};
AnnouncementsPageContent.displayName = "AnnouncementsPageContent";